import { PdfContainer } from '@/components/pdf/PdfContainer';
import { fontStyles } from '@/components/pdf/styles';
import { PurchaseOrderBundle } from '@/services/api/contracts/catalog/bundles';
import { Product } from '@/services/api/contracts/catalog/types';
import { Company, MspDetailsForCompany } from '@/services/api/contracts/companies';
import { PoStatus } from '@/services/purchaseOrders/usePurchaseOrderService';
import { Document, Text, View } from '@react-pdf/renderer';
import { FunctionComponent, useMemo } from 'react';
import PurchaseOrderDocumentDeliverySection from './PurchaseOrderDocumentDeliverySection';
import PurchaseOrderDocumentOrder from './PurchaseOrderDocumentOrder';
import PurchaseOrderDocumentPcmSection from './PurchaseOrderDocumentPcmSection';
import PurchaseOrderDocumentQuotation from './PurchaseOrderDocumentQuotation';
import PurchaseOrderDocumentSummarySection from './PurchaseOrderDocumentSummarySection';
import PurchaseOrderCompleteDocumentDeviceFeesTable, {
  PurchaseOrderCompleteDocumentDeviceFeesTableData
} from './deviceFee/table/PurchaseOrderCompleteDocumentDeviceFeesTable';
import PurchaseOrderCompleteDocumentSelectedDeviceFeeTable from './deviceFee/table/PurchaseOrderCompleteDocumentSelectedDeviceFeeTable';
import {
  PO_COMPLETE_DOCUMENT_DEVICE_FEES_FIRST_PAGE_TABLE_ROW_LENGTH,
  PO_COMPLETE_DOCUMENT_DEVICE_FEES_OTHER_PAGE_TABLE_ROW_LENGTH
} from './deviceFee/table/consts';
import PurchaseOrderCompleteDocumentTable, {
  PurchaseOrderCompleteDocumentTableData
} from './productTable/PurchaseOrderCompleteDocumentTable';
import {
  PO_COMPLETE_DOCUMENT_FIRST_PAGE_FIT_ALL_CONTENT_THRESHOLD,
  PO_COMPLETE_DOCUMENT_FIRST_PAGE_TABLE_ROW_LENGTH,
  PO_COMPLETE_DOCUMENT_OTHER_PAGE_FIT_ALL_CONTENT_THRESHOLD,
  PO_COMPLETE_DOCUMENT_OTHER_PAGE_TABLE_ROW_LENGTH
} from './productTable/consts';

const canFitAllContentOnProductPage = (pageCount: number, firstPage?: boolean) => {
  return (
    pageCount <=
    (firstPage
      ? PO_COMPLETE_DOCUMENT_FIRST_PAGE_FIT_ALL_CONTENT_THRESHOLD
      : PO_COMPLETE_DOCUMENT_OTHER_PAGE_FIT_ALL_CONTENT_THRESHOLD)
  );
};

interface Props {
  order: PurchaseOrderBundle;
  selectedDeviceFee?: Product;
  data: PurchaseOrderCompleteDocumentTableData[];
  totalPrice: number;
  totalDeviceFee: number;
  totalPcm: number;
  msp: MspDetailsForCompany;
  customer: Company;
  regionCode?: string;
  brandLogoUrl: string | null;
}

const PurchaseOrderCompleteDocument: FunctionComponent<Props> = ({
  order,
  selectedDeviceFee,
  data,
  totalPrice,
  totalPcm,
  totalDeviceFee,
  msp,
  customer,
  regionCode = 'GB',
  brandLogoUrl
}) => {
  const productTableDataPages: PurchaseOrderCompleteDocumentTableData[][] = useMemo(() => {
    const pages: PurchaseOrderCompleteDocumentTableData[][] = [[]];
    data.forEach(row => {
      let currentPage = pages[pages.length - 1];
      const pageLimit =
        pages.length === 1
          ? PO_COMPLETE_DOCUMENT_FIRST_PAGE_TABLE_ROW_LENGTH
          : PO_COMPLETE_DOCUMENT_OTHER_PAGE_TABLE_ROW_LENGTH;

      if (currentPage.length === pageLimit) {
        currentPage = [];
        pages.push(currentPage);
      }

      currentPage.push(row);
    });

    return pages;
  }, [data, PO_COMPLETE_DOCUMENT_FIRST_PAGE_TABLE_ROW_LENGTH, PO_COMPLETE_DOCUMENT_OTHER_PAGE_TABLE_ROW_LENGTH]);

  const mainDeviceData = data
    .filter(item => item.product.category.isMainDevice)
    .map(item => {
      return {
        name: item.name,
        qty: item.qty,
        price: selectedDeviceFee?.price?.price ?? 0,
        totalPrice: item.qty * (selectedDeviceFee?.price?.price ?? 0)
      };
    });

  const deviceFeesTableDataPages: PurchaseOrderCompleteDocumentDeviceFeesTableData[][] = useMemo(() => {
    const pages: PurchaseOrderCompleteDocumentDeviceFeesTableData[][] = [[]];
    mainDeviceData.forEach(row => {
      let currentPage = pages[pages.length - 1];
      const pageLimit =
        pages.length === 1
          ? PO_COMPLETE_DOCUMENT_DEVICE_FEES_FIRST_PAGE_TABLE_ROW_LENGTH
          : PO_COMPLETE_DOCUMENT_DEVICE_FEES_OTHER_PAGE_TABLE_ROW_LENGTH;

      if (currentPage.length === pageLimit) {
        currentPage = [];
        pages.push(currentPage);
      }

      currentPage.push(row);
    });

    return pages;
  }, [
    mainDeviceData,
    PO_COMPLETE_DOCUMENT_DEVICE_FEES_FIRST_PAGE_TABLE_ROW_LENGTH,
    PO_COMPLETE_DOCUMENT_DEVICE_FEES_OTHER_PAGE_TABLE_ROW_LENGTH
  ]);

  const canFitAllOnOnePage = useMemo(() => {
    return productTableDataPages.length === 1 && canFitAllContentOnProductPage(productTableDataPages[0].length, true);
  }, [productTableDataPages, deviceFeesTableDataPages]);

  const totalPages = useMemo(() => {
    return canFitAllOnOnePage
      ? 1
      : canFitAllContentOnProductPage(productTableDataPages[productTableDataPages.length - 1].length, false)
      ? productTableDataPages.length > 1
        ? productTableDataPages.length
        : 2
      : productTableDataPages.length + deviceFeesTableDataPages.length;
  }, [canFitAllOnOnePage, productTableDataPages, deviceFeesTableDataPages]);

  const summarySection = () => {
    return (
      <>
        <PurchaseOrderDocumentSummarySection
          totalProductPrice={totalPrice}
          totalDeviceFeePrice={totalDeviceFee}
          regionCode={regionCode}
        />

        {totalPcm && (
          <PurchaseOrderDocumentPcmSection monthlyPcmPrice={totalPcm} order={order} regionCode={regionCode} />
        )}
        {order.state === PoStatus.Draft && (
          <Text style={{ fontSize: 10, marginTop: 20 }}>
            *E&OE, Terms and Conditions apply. Prices may change inline with monthly catalogue updates.
          </Text>
        )}
      </>
    );
  };

  const deviceFeeSingularPageSection = () => {
    return (
      <>
        {selectedDeviceFee && (
          <View style={{ marginTop: 10 }}>
            <Text style={{ ...fontStyles.bold, fontSize: 18, marginBottom: 2 }}>Device Fees</Text>
            <PurchaseOrderCompleteDocumentSelectedDeviceFeeTable deviceFeeItem={selectedDeviceFee} />
            <PurchaseOrderCompleteDocumentDeviceFeesTable data={deviceFeesTableDataPages[0]} regionCode={regionCode} />
          </View>
        )}
      </>
    );
  };

  // Small enough to display all on one page
  const deviceFeeAndSummarySection = () => {
    return (
      <>
        {deviceFeeSingularPageSection()}
        {summarySection()}
      </>
    );
  };

  return (
    <Document>
      <PdfContainer
        pageCount={totalPages}
        pageNumber={1}
        brandLogoUrl={brandLogoUrl}
        topRightSection={
          order.state === PoStatus.Draft ? (
            <PurchaseOrderDocumentQuotation orderName={order.name} />
          ) : (
            <PurchaseOrderDocumentOrder order={order} />
          )
        }
      >
        <View style={{ marginTop: 1 }}>
          <PurchaseOrderDocumentDeliverySection order={order} msp={msp} customer={customer} />

          <View style={{ marginTop: 10 }}>
            <Text style={{ ...fontStyles.bold, fontSize: 18, marginBottom: 2 }}>Products & Services</Text>

            <PurchaseOrderCompleteDocumentTable data={productTableDataPages[0]} regionCode={regionCode} />

            {canFitAllOnOnePage && deviceFeeAndSummarySection()}
          </View>
        </View>
      </PdfContainer>
      {!canFitAllOnOnePage && (
        <>
          {productTableDataPages.length > 1 ? (
            productTableDataPages.slice(1).map((page, index) => (
              <PdfContainer
                pageCount={totalPages}
                pageNumber={2 + index}
                key={`page-${index}`}
                brandLogoUrl={brandLogoUrl}
              >
                <PurchaseOrderCompleteDocumentTable data={page} regionCode={regionCode} />

                {index === productTableDataPages.length - 2 &&
                  canFitAllContentOnProductPage(page.length, false) &&
                  deviceFeeAndSummarySection()}
              </PdfContainer>
            ))
          ) : (
            <PdfContainer pageCount={totalPages} pageNumber={2} brandLogoUrl={brandLogoUrl}>
              {deviceFeeAndSummarySection()}
            </PdfContainer>
          )}
          {!canFitAllContentOnProductPage(productTableDataPages[productTableDataPages.length - 1].length, false) && (
            <>
              <PdfContainer
                pageCount={totalPages}
                pageNumber={productTableDataPages.length + 1}
                brandLogoUrl={brandLogoUrl}
              >
                {selectedDeviceFee && deviceFeeSingularPageSection()}

                {deviceFeesTableDataPages.length === 1 && summarySection()}
              </PdfContainer>

              {deviceFeesTableDataPages.slice(1).map((page, index) => (
                <PdfContainer
                  pageCount={totalPages}
                  pageNumber={productTableDataPages.length + 2 + index}
                  key={`page-${index}`}
                  brandLogoUrl={brandLogoUrl}
                >
                  <PurchaseOrderCompleteDocumentDeviceFeesTable data={page} regionCode={regionCode} />

                  {index === deviceFeesTableDataPages.length - 2 && summarySection()}
                </PdfContainer>
              ))}
            </>
          )}
        </>
      )}
    </Document>
  );
};

export default PurchaseOrderCompleteDocument;
